import type { GridColDef } from '@mui/x-data-grid';
import type { InvoiceOverview } from '@weft/api-definition';
import type { InvoiceReviewStatus, Period } from '@weft/types';
import { INVOICE_REVIEW_STATUSES } from '@weft/types';
import type { ValueOf } from '@weft/utils';
import { dayjs, isNotNullOrUndefined, raiseUnreachable } from '@weft/utils';
import { VyTableCell } from 'apps/app/src/components/new-design/table/vy-table/VyTableCell';
import { compareDatesDescending, formatDate } from 'apps/app/src/utils/date';
import type { FieldPath } from 'react-hook-form';
import { Price } from '../../../../../../components/design/Price';
import { getBillingStatusLabel, InvoiceBillingStatusLabel } from '../../InvoiceBillingStatusLabel';
import { InvoiceSyncStatusLabel } from '../../InvoiceSyncStatusLabel/InvoiceSyncStatusLabel';
import { InvoiceOverviewTableDotMenu } from '../InvoiceOverviewTableDotMenu';

export const INVOICES_COLUMNS_KEYS = Object.freeze({
  STATUS: 'billingStatus',
  WORKFLOW: 'reviewStatus',
  ID: 'id',
  CUSTOMER: 'customer',
  ISSUE_AT: 'billingCycle',
  TOTAL_AMOUNT: 'revenueBreakdown.total',
  SYNCED: 'syncStatus',
  PAYMENT_INFO: 'paymentInfo',
  DOT_MENU: 'dotMenu',
  CONTRACT_NAME: 'contractName',
} satisfies Record<string, FieldPath<InvoiceOverview> | 'dotMenu'>);

export type InvoicesColumnsKey = ValueOf<typeof INVOICES_COLUMNS_KEYS>;

export const INVOICE_OVERVIEW_TABLE_COLUMNS: GridColDef<InvoiceOverview>[] = [
  {
    field: INVOICES_COLUMNS_KEYS.STATUS,
    valueGetter: ({ row }) => row.billingStatus,
    valueFormatter: ({ value }) => getBillingStatusLabel(value),
    headerName: 'Status',
    flex: 1,
    renderCell: ({ value }) => (
      <InvoiceBillingStatusLabel billingStatus={value}/>
    ),
  },
  {
    field: INVOICES_COLUMNS_KEYS.TOTAL_AMOUNT,
    headerName: 'Amount',
    flex: 1,
    renderCell: (params) => {
      const amount = params.row.revenueBreakdown.total;
      const currency = params.row.currency;

      return (
        <span className="text-label font-sans">
          <Price
            className="font-sans text-black"
            fontSize={14}
            currency={currency}
          >
            {amount}
          </Price>
        </span>
      );
    },
  },
  {
    field: INVOICES_COLUMNS_KEYS.CUSTOMER,
    valueGetter: ({ row }) => row.customer.name,
    headerName: 'Customer',
    flex: 1,
  },
  {
    field: INVOICES_COLUMNS_KEYS.ISSUE_AT,
    headerName: 'Date',
    flex: 1,
    valueGetter: ({ row }) => formatDate(dayjs(row.billingCycle.startTime)),
    renderCell: ({ row }) => formatDate(dayjs(row.billingCycle.startTime)),
    sortComparator: (billingCycle1: Period, billingCycle2: Period) => compareDatesDescending(billingCycle1.startTime, billingCycle2.startTime),
  },
  {
    field: INVOICES_COLUMNS_KEYS.ID,
    headerName: 'Invoice',
    flex: 1,
    renderCell: ({ value }) => <span className="font-sans text-black">{value?.slice(-5)}</span>,
  },
  {
    field: 'customerExternalId',
    headerName: 'Customer External Id',
    valueGetter: ({ row }) => row.customer.externalId,
  },
  {
    field: 'customerErpId',
    headerName: 'Customer ERP Id',
    valueGetter: ({ row }) => row.customer.erpMetadata?.erpId,
  },
  {
    field: INVOICES_COLUMNS_KEYS.WORKFLOW,
    headerName: 'Workflow',
    flex: 1,
    renderCell: ({ value }) => {
      return (
        <div className="flex items-center justify-center">
          <p className="font-sans text-black">{getReviewStatusLabel(value)}</p>
        </div>
      );
    },
  },
  {
    field: INVOICES_COLUMNS_KEYS.SYNCED,
    headerName: 'ERP Sync',
    headerAlign: 'center',
    flex: 1,
    renderCell: (params) => {
      const syncStatus = params.row.syncStatus;
      const syncStatusUpdatedAt = params.row.syncStatusUpdatedAt;

      if (isNotNullOrUndefined(syncStatus) && isNotNullOrUndefined(syncStatusUpdatedAt)) {
        const syncStatusUpdatedAtDate = formatDate(dayjs(syncStatusUpdatedAt), 'MMM. D, YY HH:mm z');

        return (
          <InvoiceSyncStatusLabel
            className='flex w-full items-center justify-center'
            syncStatus={syncStatus}
            syncStatusUpdatedAt={syncStatusUpdatedAtDate}
          />
        );
      }

      return <span/>;
    },
  },
  {
    field: INVOICES_COLUMNS_KEYS.PAYMENT_INFO,
    headerName: 'Payment',
    headerAlign: 'center',
    flex: 1,
    renderCell: ({ row }) => (
      <VyTableCell value={row.paymentInfo?.transactionId}/>
    ),
  },
  {
    field: INVOICES_COLUMNS_KEYS.CONTRACT_NAME,
    headerName: 'Contract',
    flex: 1,
  },
  {
    field: INVOICES_COLUMNS_KEYS.DOT_MENU,
    headerName: '',
    disableExport: true,
    renderCell: ({ row }) => (
      <InvoiceOverviewTableDotMenu invoice={row}/>
    ),
  },
];

export function getReviewStatusLabel(reviewStatus: InvoiceReviewStatus) {
  switch (reviewStatus) {
    case INVOICE_REVIEW_STATUSES.Approved:
      return 'Approved';
    case INVOICE_REVIEW_STATUSES.InReview:
      return 'In review';
    case INVOICE_REVIEW_STATUSES.Draft:
      return 'Draft';
    case INVOICE_REVIEW_STATUSES.Synced:
      return 'Synced';
    default:
      return raiseUnreachable(reviewStatus);
  }
}